import type { ReactNode } from 'react'
import { Navigate, useParams } from 'react-router-dom'

import { ContentLoading } from 'src/components'
import { useFeatureFlagsServerSettled } from 'src/feature-flags'

import { paths } from './paths'

/**
 * Gates a workspace route behind an Unleash flag. When the flag is OFF the page
 * is unreachable — even via a deep link — and the user is redirected to the
 * workspace home, mirroring the sidebar (which hides the matching nav item).
 * Pass one of the `useFeature*` hooks from `src/flags` as `useFlag`.
 *
 * The decision waits on `useFeatureFlagsServerSettled` (NOT the weaker
 * `useFeatureFlagsReady`): the client reports "ready" from the OFF bootstrap
 * before it has fetched, so redirecting on that would bounce a refreshed /
 * deep-linked session away from a feature that is actually enabled for its
 * environment.
 */
export const WorkspaceFeatureRoute = ({
  useFlag,
  children
}: {
  useFlag: () => boolean
  children: ReactNode
}) => {
  const settled = useFeatureFlagsServerSettled()
  const enabled = useFlag()
  const { id } = useParams<{ id: string }>()

  if (!settled) {
    return <ContentLoading />
  }

  if (!enabled) {
    return (
      <Navigate
        to={id ? paths.workspaces.home(id) : paths.workspaces.root}
        replace
      />
    )
  }

  return <>{children}</>
}
